"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { ClipboardCheck, Loader2, ArrowRight } from "lucide-react";

const PARTNER_REVIEW_FILE = "PartnerReview.json";

type ConcludingQuestionType = "confirmed" | "yesno";

const CONCLUDING_QUESTIONS: Array<{
  id: string;
  text: string;
  type: ConcludingQuestionType;
}> = [
  {
    id: "PR-C6",
    type: "confirmed",
    text: "Have you as an engagement partner taken overall responsibility for managing and achieving quality on the audit engagement and determined that:\ni. Your involvement has been sufficient and appropriate throughout the audit engagement such that you have the basis for determining that the significant judgements made and the conclusions reached are appropriate given the nature and circumstances of the engagement",
  },
  {
    id: "PR-C7",
    type: "confirmed",
    text: "Have you as an engagement partner taken overall responsibility for managing and achieving quality on the audit engagement through direction and supervision of members of the engagement team and review their work?",
  },
  {
    id: "PR-C8",
    type: "confirmed",
    text: "Have you as an engagement partner, determined that the nature, timing and extent of direction, supervision and review of the engagement team is:\ni. Planned and performed in accordance with the firm's policies or procedures, professional standards, and applicable legal and regulatory requirements; and\nii. Responsive to the nature and circumstances of the audit engagement and resources assigned or made available to the engagement team by the firm?",
  },
  {
    id: "PR-C9",
    type: "confirmed",
    text: "Have you as an engagement partner performed a secondary review of the audit documentation at the appropriate points in the time related to significant matters, significant judgements, and other matters that, in your professional judgement, are relevant to your responsibilities?",
  },
  {
    id: "PR-C10",
    type: "yesno",
    text: "Have you as the engagement partner obtained assistance from other partners who are members of the engagement team for all or a portion of such secondary review? [Yes/No]",
  },
  {
    id: "PR-C11",
    type: "confirmed",
    text: "Are you as the engagement partner satisfied that sufficient appropriate audit evidence has been obtained to support conclusions reached and for the report to be issued?",
  },
  {
    id: "PR-C12",
    type: "confirmed",
    text: "Have you as the engagement partner, reviewed the financial statements and the auditor's report and the related documentation, to determine that the report to be issued will be appropriate in the circumstances?",
  },
  {
    id: "PR-C13",
    type: "confirmed",
    text: "Have you as the engagement partner reviewed prior to the issuance formal written communications to the management and those charged with governance/audit committee or regulatory authorities?",
  },
  {
    id: "PR-C14",
    type: "confirmed",
    text: "Have you as the engagement partner determined that:\ni. Determined that an engagement quality reviewer has been appointed,\nii. Cooperated with the engagement quality reviewer and informed other members of the engagement team of their responsibility to do so?\niii. Discussed significant matters and significant judgements arising during the audit engagement, including those identified during the engagement quality review, with the engagement quality reviewer?\niv. Not dated audit report until the completion of the engagement quality review?",
  },
  {
    id: "PR-C15",
    type: "confirmed",
    text: "Have you as an engagement partner:\ni. Obtained an understanding of the information from the firm's monitoring and remediation process, as communicated by the firm including, as applicable, the information from the monitoring and remediation process of the network and across the network firms?\nii. Determined the relevance and effect on the audit engagement of such information and taken appropriate action?\niii. Remained alert throughout the audit engagement for information that may be relevant to the firm's monitoring and remediation process and communicate such information to those responsible for the process?",
  },
  {
    id: "PR-C16",
    type: "confirmed",
    text: "Confirm that:\ni. A detailed review of each element of audit documentation, except the audit documentation prepared by the engagement partner, was performed by a member of the engagement team who has more experience that the preparer.\nii. A secondary review of the audit documentation was performed by the audit manager, engagement partner, or other suitably qualified person, including documenting the extent of such review.",
  },
];

type ConcludingReviewState = Record<string, boolean | string>;

interface PartnerReviewConcludingProps {
  setActiveSection?: (section: string) => void;
  onSelect?: (section: string) => void;
}

export function PartnerReviewConcluding({ setActiveSection, onSelect }: PartnerReviewConcludingProps = {}) {
  const [state, setState] = useState<ConcludingReviewState>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "error">("idle");

  const loadResponses = useCallback(async () => {
    setLoading(true);
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson(PARTNER_REVIEW_FILE);
        if (result?.success && result.data?.concludingReview) {
          const cr = result.data.concludingReview as Record<string, boolean | string>;
          const next: ConcludingReviewState = {};
          CONCLUDING_QUESTIONS.forEach((q) => {
            if (q.type === "yesno") {
              next[q.id] = cr[q.id] === "Yes" || cr[q.id] === "No" ? (cr[q.id] as string) : "";
            } else {
              next[q.id] = !!cr[q.id];
            }
          });
          setState(next);
        } else {
          CONCLUDING_QUESTIONS.forEach((q) => {
            setState((prev) => ({
              ...prev,
              [q.id]: q.type === "yesno" ? "" : false,
            }));
          });
        }
      } else {
        CONCLUDING_QUESTIONS.forEach((q) => {
          setState((prev) => ({
            ...prev,
            [q.id]: q.type === "yesno" ? "" : false,
          }));
        });
      }
    } catch (error) {
      console.error("Failed to load PartnerReview.json", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  const saveResponses = useCallback(async (nextState: ConcludingReviewState) => {
    setSaving(true);
    setSaveStatus("idle");
    try {
      let existingData: Record<string, unknown> = {};
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const res = await (window as any).sharePointAPI.downloadClientJson(PARTNER_REVIEW_FILE);
        if (res?.success && res.data && typeof res.data === "object") {
          existingData = res.data as Record<string, unknown>;
        }
      }

      const concludingReview: Record<string, boolean | string> = {};
      CONCLUDING_QUESTIONS.forEach((q) => {
        const v = nextState[q.id];
        if (q.type === "yesno") {
          concludingReview[q.id] = v === "Yes" || v === "No" ? (v as string) : "";
        } else {
          concludingReview[q.id] = !!v;
        }
      });

      const payload = {
        ...existingData,
        concludingReview,
      };

      if ((window as any).sharePointAPI?.uploadClientJson) {
        const uploadRes = await (window as any).sharePointAPI.uploadClientJson(
          PARTNER_REVIEW_FILE,
          payload
        );
        if (uploadRes?.success) {
          setSaveStatus("saved");
        } else {
          setSaveStatus("error");
        }
      } else {
        setSaveStatus("error");
      }
    } catch (error) {
      console.error("Failed to save PartnerReview.json", error);
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  }, []);

  const handleConfirmedChange = (questionId: string, checked: boolean) => {
    const next = { ...state, [questionId]: checked };
    setState(next);
    saveResponses(next);
  };

  const handleYesNoChange = (questionId: string, value: string) => {
    const next = { ...state, [questionId]: value };
    setState(next);
    saveResponses(next);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6 flex items-center justify-center">
        <div className="flex items-center gap-2 text-white/70">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading concluding procedure review...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Detailed Concluding Review Header */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Detailed Concluding Procedure Review</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const navigate = setActiveSection || onSelect;
                  navigate?.("detailed-concluding-review-dashboard");
                }}
                className="border-white/20 bg-white/5 text-white hover:bg-white/10"
                data-allow-archival-edit={true}
              >
                View Dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-blue-600/20 flex items-center justify-center">
                  <ClipboardCheck className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Concluding Procedure Review</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Partner review of concluding procedures</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {saving && (
                  <span className="text-xs text-white/50 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                  </span>
                )}
                {saveStatus === "saved" && !saving && (
                  <span className="text-xs text-green-400 flex items-center gap-1">Saved</span>
                )}
                {saveStatus === "error" && !saving && (
                  <span className="text-xs text-red-400">Save failed</span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {CONCLUDING_QUESTIONS.map((q, index) => (
              <div
                key={q.id}
                className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3"
              >
                <div className="flex gap-3">
                  <span className="text-sm font-medium text-white/60 shrink-0">{index + 1}.</span>
                  <p className="text-sm text-white/90 whitespace-pre-line flex-1">{q.text}</p>
                </div>
                {q.type === "yesno" ? (
                  <div className="pl-6">
                    <RadioGroup
                      value={state[q.id] === "Yes" || state[q.id] === "No" ? (state[q.id] as string) : ""}
                      onValueChange={(val) => handleYesNoChange(q.id, val)}
                      className="flex gap-6"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem
                          value="Yes"
                          id={`${q.id}-yes`}
                          className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                        />
                        <Label htmlFor={`${q.id}-yes`} className="text-sm text-white/80 cursor-pointer">
                          Yes
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem
                          value="No"
                          id={`${q.id}-no`}
                          className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                        />
                        <Label htmlFor={`${q.id}-no`} className="text-sm text-white/80 cursor-pointer">
                          No
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 pl-6">
                    <Checkbox
                      id={q.id}
                      checked={!!state[q.id]}
                      onCheckedChange={(c) => handleConfirmedChange(q.id, c === true)}
                      className="border-white/20 data-[state=checked]:bg-[#4da3ff] data-[state=checked]:border-[#4da3ff]"
                    />
                    <Label
                      htmlFor={q.id}
                      className="text-sm text-white/80 cursor-pointer font-medium"
                    >
                      Confirmed
                    </Label>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
